import type { PlatformSummary } from '@porttang/core';
import { useEffect, useRef } from 'react';
import {
  EM_DASH,
  formatPercent,
  formatSharePercent,
  formatSignedTHB,
  formatTHB,
  toneClassFor,
} from '../../shared/format';
import { Card, SectionLabel, tintFor } from '../../shared/pixel';

/**
 * แพลตฟอร์มที่ยังตีมูลค่าไม่ได้สักรายการ — ยอด 0 จากตัวรวมไม่ได้แปลว่าไม่มีของ
 * ต้องแสดง `—` ไม่ใช่ ฿0 (spec Section 9.3)
 */
export function isUnvalued(summary: PlatformSummary): boolean {
  return summary.totals.incompleteCount === summary.totals.holdingCount;
}

/**
 * แท็บเลือกพอร์ต — "รวมทุกพอร์ต" หรือทีละแพลตฟอร์ม
 *
 * แถบเลื่อนแนวนอนได้ในกล่องของตัวเอง แพลตฟอร์มเยอะแล้วบนมือถือจะไม่ดันทั้งหน้าให้ล้นจอ
 * สีจุดหน้าชื่อผูกกับชื่อแพลตฟอร์ม — ตรงกับสีในวงแหวนตอนแบ่งตามแพลตฟอร์ม
 */
export function PlatformTabs({
  platforms,
  selected,
  onSelect,
}: {
  platforms: readonly PlatformSummary[];
  selected: string | null;
  onSelect: (platform: string | null) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // ลำดับแท็บเปลี่ยนได้โดยที่แท็บที่เลือกไม่เปลี่ยน — ตอนราคายังไม่มา มูลค่าเป็นศูนย์หมดจึงเรียงตามชื่อ
  // พอราคามาถึงก็เรียงใหม่ตามมูลค่า แท็บที่เลือกย้ายตำแหน่งไปแล้ว ต้องเลื่อนตามไปด้วย
  const order = platforms.map((item) => item.platform).join('\n');

  /*
    แท็บที่เลือกต้องมองเห็นเสมอ — บนมือถือแท็บท้าย ๆ อยู่นอกจอ เปิดลิงก์ `?platform=` ตรง ๆ
    หรือกดแถวแพลตฟอร์มแล้วจะไม่รู้ว่ากำลังดูพอร์ตไหนอยู่

    เลื่อนเฉพาะแนวนอนในกล่องของแท็บ ไม่ใช้ `scrollIntoView` เพราะมันลากทั้งหน้าไปด้วย
    และไปตัดการเลื่อนกลับขึ้นบนตอนกดแถวแพลตฟอร์มกลางหน้า
  */
  useEffect(() => {
    const scroller = scrollerRef.current;
    const active = scroller?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (scroller === null || active === null || active === undefined) {
      return;
    }
    const left = active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [selected, order]);

  const tabs = [
    { key: null, label: 'รวมทุกพอร์ต' },
    ...platforms.map((item) => ({ key: item.platform, label: item.platform })),
  ];

  return (
    // `relative` ให้ offsetLeft ของแท็บนับจากกล่องนี้ ไม่ใช่จากทั้งหน้า
    <div ref={scrollerRef} className="relative overflow-x-auto">
      <div
        role="tablist"
        aria-label="เลือกพอร์ต"
        className="flex w-max gap-1 rounded-lg border border-line-strong bg-sunken p-1"
      >
        {tabs.map((tab) => {
          const active = tab.key === selected;
          return (
            <button
              key={tab.key ?? ''}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(tab.key)}
              className={[
                'flex items-center gap-2 rounded-[5px] px-3 py-1.5 text-[13px] whitespace-nowrap transition',
                active ? 'bg-brand-sel text-brand-text' : 'text-ink-400 hover:text-ink-100',
              ].join(' ')}
            >
              {tab.key !== null && (
                <span
                  aria-hidden
                  className="size-2 shrink-0"
                  style={{ backgroundColor: tintFor(tab.key) }}
                />
              )}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * สรุปรายแพลตฟอร์มบนแท็บพอร์ตรวม — กดแถวไหนก็เข้าแท็บของแพลตฟอร์มนั้น
 *
 * วางคอลัมน์ตัวเลขความกว้างตายตัวแบบเดียวกับตารางสินทรัพย์บนมือถือ
 * ตัวเลขทุกแถวจะตรงแนวเดียวกัน กวาดตาลงมาเทียบกันได้ทันที
 */
export function PlatformBreakdown({
  platforms,
  onSelect,
}: {
  platforms: readonly PlatformSummary[];
  onSelect: (platform: string) => void;
}) {
  const anyCash = platforms.some((item) => item.hasCash);

  return (
    <Card className="overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <SectionLabel>PLATFORMS · {platforms.length}</SectionLabel>
        <span className="text-[11px] text-ink-500">กดเพื่อดูพอร์ตแยก</span>
      </header>

      <div className="flex items-end gap-2 border-b border-line-soft px-4 pt-3 pb-2 font-mono text-[10.5px] tracking-[0.5px] text-ink-500 sm:px-5">
        <span className="min-w-0 flex-1">แพลตฟอร์ม</span>
        <span className="w-[112px] text-right sm:w-[170px]">มูลค่า (THB)</span>
        <span className="w-[72px] text-right sm:w-[130px]">กำไร/ขาดทุน</span>
      </div>

      <ul className="divide-y divide-line-soft">
        {platforms.map((item) => {
          const unvalued = isUnvalued(item);

          return (
            <li key={item.platform}>
              <button
                type="button"
                onClick={() => onSelect(item.platform)}
                className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-hover sm:px-5"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0"
                    style={{ backgroundColor: tintFor(item.platform) }}
                  />
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-ink-100">{item.platform}</div>
                    <div className="truncate font-mono text-[10px] text-ink-500">
                      {item.totals.holdingCount} รายการ
                      {item.totals.incompleteCount > 0 &&
                        ` · ไม่มีราคา ${item.totals.incompleteCount}`}
                    </div>
                  </div>
                </div>

                <div className="w-[112px] shrink-0 text-right sm:w-[170px]">
                  <div className="font-mono text-[14px] font-semibold text-ink-100">
                    {unvalued ? EM_DASH : formatTHB(item.totals.valueTHB)}
                  </div>
                  <div className="font-mono text-[10px] text-ink-500">
                    {unvalued ? EM_DASH : `${formatSharePercent(item.sharePct)} ของพอร์ต`}
                  </div>
                </div>

                <div
                  className={`w-[72px] shrink-0 text-right sm:w-[130px] ${
                    unvalued ? 'text-ink-400' : toneClassFor(item.invested.pnlTHB)
                  }`}
                >
                  <div className="font-mono text-[14px] font-semibold">
                    {formatPercent(item.invested.pnlPct)}
                  </div>
                  <div className="truncate font-mono text-[10px] opacity-80">
                    {unvalued ? EM_DASH : formatSignedTHB(item.invested.pnlTHB)}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {anyCash && (
        <p className="border-t border-line-soft px-5 py-2.5 text-[11px] text-ink-500">
          กำไร/ขาดทุนและ % ไม่รวมเงินสด
        </p>
      )}
    </Card>
  );
}
